<script setup>
/**
 * Their four cards, dealt at a dealer's pace (lesson-redo DESIGN.md F, H4).
 *
 * Wide: four equal centred columns, 104px cards, every line centred, column
 * tops aligned. Phone: one centred column, 96px cards. Each beat: the card
 * (or, for astrology, the placement's star), the position (Before, Now,
 * Ahead, What helps), the card's name, its beat.
 *
 * The deal: cards deal as they come into view (all four in sequence on a
 * wide row, one by one down a phone), no tap needed. Each flips in 0.8s; the
 * name fades in at 0.8s and the beat at 0.95s, once the card has landed; the
 * next starts 1.25s after the last, so four take about five seconds. The
 * turning card keeps "✦ the turn" and gains a soft gold glow once it lands.
 * Reduced motion (or no IntersectionObserver): everything is simply shown.
 *
 * Emits `turned` (how many are face up, for the sky's stars) and `dealt`.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { storyBeats } from '@/utils/lesson'

const props = defineProps({ lesson: { type: Object, required: true } })
const emit = defineEmits(['turned', 'dealt'])

// The next card starts 1.25s after the last (about five seconds for four).
const DEAL_GAP = 1250
const beats = computed(() => storyBeats(props.lesson).slice(0, 4))
const astro = computed(() => beats.value.some((b) => b.astro))
const turned = ref(0)
const failed = ref({})
const items = ref([])

const reduced = () => typeof window !== 'undefined' && !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)

let io = null
let timer = null
let dealTo = -1
function stop() {
  if (io) { io.disconnect(); io = null }
  if (timer) { clearTimeout(timer); timer = null }
}
function showAll() { stop(); turned.value = beats.value.length }
function deal() {
  timer = null
  if (turned.value >= beats.value.length) { stop(); return }
  if (turned.value > dealTo) return
  turned.value += 1
  timer = setTimeout(deal, DEAL_GAP)
}
function start() {
  stop()
  turned.value = 0
  dealTo = -1
  failed.value = {}
  if (reduced() || typeof window === 'undefined' || !('IntersectionObserver' in window)) { showAll(); return }
  io = new window.IntersectionObserver((entries) => {
    for (const e of entries) {
      if (!e.isIntersecting) continue
      const i = items.value.indexOf(e.target)
      if (i > dealTo) dealTo = i
    }
    if (!timer) deal()
  }, { threshold: 0.6 })
  nextTick(() => { items.value.forEach((el) => el && io && io.observe(el)) })
}
watch(turned, (n) => {
  emit('turned', n)
  if (n && n >= beats.value.length) emit('dealt')
})
// A related lesson loaded in place deals again.
watch(() => props.lesson && props.lesson.short_uuid, () => start())
onMounted(start)
onBeforeUnmount(stop)
defineExpose({ showAll, turned })
</script>

<template>
  <section v-if="beats.length" class="sty" data-testid="lesson-story" :aria-label="astro ? 'Their story, in four placements' : 'Their story, in four cards'">
    <ol class="st-row">
      <li v-for="(b, i) in beats" :key="i" :ref="(el) => { items[i] = el }" class="beat" :class="{ on: i < turned, turning: b.turn }"
          data-testid="story-beat">
        <span v-if="b.astro" class="star-b" aria-hidden="true"><span class="glyph">{{ b.glyph }}</span></span>
        <span v-else class="flip" aria-hidden="true">
          <span class="face back"></span>
          <span class="face front"><img v-if="b.image_url && !failed[i]" :src="b.image_url" alt="" :class="{ rev: b.reversed }"
                                         @error="failed = { ...failed, [i]: true }" /></span>
        </span>
        <div class="beat-t">
          <p class="pos" data-testid="lesson-card-role">{{ b.pos }}{{ b.turn ? ' ' : '' }}<span v-if="b.turn" class="turnmk">✦ the turn</span></p>
          <p v-if="b.name" class="nm">{{ b.name }}</p>
          <p class="ln" data-testid="lesson-card-teaches">{{ b.line }}</p>
        </div>
      </li>
    </ol>
  </section>
</template>

<style scoped>
.sty {
  --gold: #c9a94e; --golddk: #8a6a2a; --ink: #2a1845; --muted: #7a6a8f; --plum: #2a1845;
  --display: 'Letter Display', 'Cinzel', Georgia, serif; --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  --ease: cubic-bezier(.45, .05, .25, 1);
  margin: 30px 22px 0; padding-top: 24px; border-top: 1px solid rgba(201, 169, 78, .4); text-align: center;
}
.sty *, .sty *::before, .sty *::after { box-sizing: border-box; }
.st-row { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: 1fr; gap: 30px; justify-items: center; }
.beat { display: grid; grid-template-columns: 1fr; justify-items: center; align-content: start; gap: 12px; max-width: 320px; width: 100%; }
.beat-t { min-width: 0; }

/* The card: its back (the house pattern) turns to its face. */
.flip { position: relative; display: block; width: 96px; aspect-ratio: 2 / 3; perspective: 800px; }
.face { position: absolute; inset: 0; border-radius: 6px; overflow: hidden; border: 1px solid rgba(201, 169, 78, .9);
  backface-visibility: hidden; -webkit-backface-visibility: hidden; box-shadow: 0 10px 16px -10px rgba(42, 24, 69, .9);
  transition: transform .8s var(--ease); }
.face.back { background:
  radial-gradient(circle at 50% 50%, rgba(231, 201, 131, .9) 0 3px, transparent 4px),
  repeating-linear-gradient(45deg, rgba(231, 201, 131, .16) 0 1px, transparent 1px 9px),
  repeating-linear-gradient(-45deg, rgba(231, 201, 131, .16) 0 1px, transparent 1px 9px),
  linear-gradient(160deg, #3d2266, #1b0d33); }
.face.front { transform: rotateY(180deg); background: var(--plum); }
.face.front img { width: 100%; height: 100%; object-fit: cover; display: block; }
.face.front img.rev { transform: rotate(180deg); }
.beat.on .face.back { transform: rotateY(-180deg); }
.beat.on .face.front { transform: rotateY(0deg); }
/* The turning card: a soft gold glow once it lands. */
.beat.turning .face.front { transition: transform .8s var(--ease), box-shadow .8s ease .7s; }
.beat.turning.on .face.front { box-shadow: 0 0 0 2px rgba(231, 201, 131, .9), 0 0 28px 6px rgba(231, 201, 131, .55), 0 10px 16px -10px rgba(42, 24, 69, .9); }

/* Astrology: a star that lights as its placement turns. */
.star-b { width: 96px; height: 96px; border-radius: 50%; display: grid; place-items: center; border: 1px solid rgba(201, 169, 78, .6);
  background: radial-gradient(circle, #2a1548, #12082a); color: #8e7fae; font: 34px/1 var(--serif); transition: color .6s, box-shadow .6s; }
.beat.on .star-b { color: #ffe9b8; box-shadow: 0 0 0 4px rgba(231, 201, 131, .18), 0 0 26px rgba(255, 233, 184, .35); }

.pos { display: flex; justify-content: center; align-items: center; flex-wrap: wrap; margin: 0; font: 600 9.5px/1.6 var(--display); letter-spacing: .18em;
  text-transform: uppercase; color: var(--golddk); }
.turnmk { margin-left: 8px; font: italic 600 13px var(--serif); letter-spacing: .02em; text-transform: none; color: #b38a2a; opacity: 0; transition: opacity .4s ease .85s; }
.beat.on .turnmk { opacity: 1; }
.nm { margin: 0 0 4px; font: italic 500 14px/1.3 var(--serif); color: var(--muted); opacity: 0; transform: translateY(4px); transition: opacity .35s ease, transform .35s ease; }
.beat.on .nm { opacity: 1; transform: none; transition-delay: .8s; }
.ln { margin: 0; font: 500 18px/1.38 var(--serif); color: var(--ink); text-wrap: pretty; overflow-wrap: anywhere;
  opacity: 0; transform: translateY(4px); transition: opacity .45s ease, transform .45s ease; }
.beat.on .ln { opacity: 1; transform: none; transition-delay: .95s; }

@container (min-width: 760px) {
  .sty { margin: 40px 72px 0; padding-top: 26px; }
  .st-row { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 26px; align-items: start; }
  .beat { max-width: none; }
  .flip { width: 104px; }
  .star-b { width: 104px; height: 104px; }
}
@media (prefers-reduced-motion: reduce) {
  .face, .nm, .ln, .turnmk, .star-b { transition: none !important; }
}
</style>
